import { ToastContainer } from '@design-system/components/atoms'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { configureHttpClient } from '@mathflat/domain/http/client'
import { configureApiBoundary } from '@mathflat/domain/schema/apiBoundary'
import { showApiBoundaryIssue } from '@mathflat/shared/@common/apiBoundaryOverlay'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { installGlobalHandlers } from '@mathflat/shared/@modules/errorReporting'
import { bindI18nBrand, initI18n } from '@mathflat/shared/i18n/i18n'
import { QueryClientProvider } from '@tanstack/react-query'
import { domAnimation, LazyMotion } from 'framer-motion'
import { createBrowserHistory } from 'history'
import { reaction } from 'mobx'
import { syncHistoryWithStore } from 'mobx-react-router'
import ReactDOM from 'react-dom/client'
import { Router } from 'react-router-dom'
import smoothscroll from 'smoothscroll-polyfill'

// import { VWOProvider } from 'vwo-fme-react-sdk'
import { Portal } from '@/component/molecules/Portal'

import App from './App'
import authDaemon from './authDaemon'
import CommonModal from './component/templates/ModalTemplate/Modal'
import queryClient from './controller/queryClient'
import commonRepo from './repository/common.repo'
import { RouteName } from './router'
import { localStorageService } from './service/storage.service'
import { viewportService } from './service/viewport.service'
import { envUtils } from './utils'
import { applyBrandHead } from './utils/applyBrandHead'
import { registerCreverseSsoInterceptor } from './utils/axios/creverseSsoInterceptor'
import { domainAiHttpClient } from './utils/axios/domainAiHttpClient'
import { domainCmsHttpClient } from './utils/axios/domainCmsHttpClient'
import { domainHomepageHttpClient } from './utils/axios/domainHomepageHttpClient'
import { domainHttpClient } from './utils/axios/domainHttpClient'
import { domainIdentifyHttpClient } from './utils/axios/domainIdentifyHttpClient'
import { domainMessagingHttpClient } from './utils/axios/domainMessagingHttpClient'
import { domainPodHttpClient } from './utils/axios/domainPodHttpClient'
import { domainPrintHttpClient } from './utils/axios/domainPrintHttpClient'
import { domainRemoteStorageHttpClient } from './utils/axios/domainRemoteStorageHttpClient'
import { domainSaiHttpClient } from './utils/axios/domainSaiHttpClient'
import { domainSearchHttpClient } from './utils/axios/domainSearchHttpClient'
import { reporter } from './utils/modules/errorReporting/reporter'
import { initSentry } from './utils/modules/errorReporting/sentry'
import navigationUtils from './utils/navigationUtils'
import { testUtils } from './utils/testUtils'

smoothscroll.polyfill()

domainProvider.debugging({
  isLiveEnv: import.meta.env.VITE_VERCEL_ENV === 'production',
})

// 에러 수집은 다른 어떤 초기화보다 먼저 — 부팅 중 예외까지 잡는다. Sentry → 전역 리스너 순서.
initSentry()
installGlobalHandlers(reporter)

configureHttpClient(domainHttpClient)
configureHttpClient(domainPodHttpClient, 'pod')
configureHttpClient(domainCmsHttpClient, 'cms')
configureHttpClient(domainMessagingHttpClient, 'messaging')
configureHttpClient(domainSearchHttpClient, 'search')
configureHttpClient(domainSaiHttpClient, 'sai')
configureHttpClient(domainPrintHttpClient, 'print')
configureHttpClient(domainAiHttpClient, 'ai')
configureHttpClient(domainHomepageHttpClient, 'homepage')
configureHttpClient(domainIdentifyHttpClient, 'identify')
configureHttpClient(domainRemoteStorageHttpClient, 'remoteStorage')

configureApiBoundary({
  // 어긋난 응답에 예외를 던지면 화면 전체가 흰 화면이 되므로 어느 환경에서도 던지지 않는다.
  strict: false,
  reporter: (issue) => {
    // e2e 에서는 오버레이가 클릭을 가로막으므로 띄우지 않는다.
    if (!envUtils.isLive && !envUtils.isStaging && !import.meta.env.VITE_E2E_DEV_SERVER) {
      showApiBoundaryIssue(issue)
    }
  },
})

// 크레버스 ssoToken 무효(400) 응답을 전역에서 잡아 로그아웃시키는 인터셉터 등록
registerCreverseSsoInterceptor()

if (!envUtils.isLive) {
  testUtils.initialize()
}

const history = syncHistoryWithStore(createBrowserHistory(), commonRepo.router)
viewportService.init(history)
const clientSideEmotionCache = createCache({ key: 'css' })

if (localStorageService.getItem('LOGIN_PATH') === null) {
  const isLoginPath =
    window.location.pathname === RouteName.login ||
    window.location.pathname === RouteName.busanLogin ||
    window.location.pathname === RouteName.jeonnamLogin ||
    window.location.pathname === RouteName.publicLogin

  localStorageService.setItem('LOGIN_PATH')(
    isLoginPath
      ? (window.location.pathname as
          | typeof RouteName.login
          | typeof RouteName.busanLogin
          | typeof RouteName.jeonnamLogin
          | typeof RouteName.publicLogin)
      : RouteName.login,
  )
}
commonRepo.router.listener.listen()

// 경로 축을 빼면 `/creverse/*` 진입 시 로그인 전 isCreverse === false 가 head 를 매쓰플랫으로 되돌린다.
// router.location 은 mobx-react-router 4 + MobX 6 조합에서 observable 이 아니라 reaction 이 추적하지 못하므로 history 구독으로 따로 갱신한다.
const syncBrandHead = () => {
  const isCreverse = commonRepo.isCreverse || history.location.pathname.startsWith('/creverse/')
  applyBrandHead(isCreverse ? 'creverse' : 'default')
}
reaction(() => commonRepo.isCreverse, syncBrandHead, { fireImmediately: true })
history.listen(syncBrandHead)

authDaemon()
  .then(() => initI18n())
  .then(() => {
    const academy = commonRepo.academy
    if (!academy) return
    return bindI18nBrand(
      { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
      academy.languagePackFileUrl,
    )
  })
  .then(() => {
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <Router history={history}>
        <CacheProvider value={clientSideEmotionCache}>
          <QueryClientProvider client={queryClient}>
            {/* <VWOProvider config={vwoConfig} userContext={getInitialUserContext()}> */}
            <LazyMotion features={domAnimation}>
              <App />
            </LazyMotion>
            <CommonModal />
            <ToastContainer />
            {/* </VWOProvider> */}
          </QueryClientProvider>
          <Portal.Container />
        </CacheProvider>
      </Router>,
    )
  })
  .catch((error) => {
    reporter.capture(error, { mechanism: 'boot' })
    console.error('App initialization error:', error)
  })

// 일렉트론 401 Error 발생시 사용할 함수 - 추후 localStorageService 키와 같게 수정해야함.
window['invalidateToken'] = function () {
  window.location.href = RouteName.login
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('x-auth-token')
  localStorage.removeItem('teacher-id')
  localStorage.removeItem('teacherId')
  sessionStorage.removeItem('accessToken')
  sessionStorage.removeItem('refreshToken')
  sessionStorage.removeItem('x-auth-token')
  sessionStorage.removeItem('teacher-id')
  sessionStorage.removeItem('teacherId')
  navigationUtils.reload()
}
